import { Fragment, FC, ReactNode } from "react";
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  Transition,
  TransitionChild,
} from "@headlessui/react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { cn } from "helpers/classNames";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  children: ReactNode;
  footer?: ReactNode;
  afterClose?: () => void;
  className?: string;
  /** Prevents closing via backdrop click / Esc. The header & footer buttons still close. */
  disableOutsideClose?: boolean;
}

/**
 * Mobile-native slide-up sheet. Anchored to the bottom of the viewport,
 * with a grip, sticky header and optional sticky footer. Backdrop / Esc
 * closes via `onClose`.
 */
export const BottomSheet: FC<Props> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  children,
  footer,
  afterClose,
  className,
  disableOutsideClose = false,
}) => (
  <Transition show={isOpen} as={Fragment} afterLeave={afterClose}>
    <Dialog
      as="div"
      className="relative z-50"
      onClose={disableOutsideClose ? () => {} : onClose}
    >
      <TransitionChild
        as={Fragment}
        enter="ease-out duration-200"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        leave="ease-in duration-200"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div className="fixed inset-0 bg-gray-900/50" />
      </TransitionChild>

      <div className="fixed inset-0 overflow-hidden">
        <div className="flex min-h-full items-end justify-center">
          <TransitionChild
            as={Fragment}
            enter="transform ease-out duration-300"
            enterFrom="translate-y-full"
            enterTo="translate-y-0"
            leave="transform ease-in duration-200"
            leaveFrom="translate-y-0"
            leaveTo="translate-y-full"
          >
            <DialogPanel
              className={cn(
                "relative flex max-h-[92vh] w-full max-w-2xl transform-gpu flex-col rounded-t-3xl bg-white shadow-2xl will-change-transform",
                className,
              )}
            >
              <div className="mx-auto mt-2.5 h-1.5 w-10 flex-none rounded-full bg-gray-200" />

              <div className="flex items-start justify-between gap-3 border-b border-gray-75 px-5 pb-3.5 pt-2">
                <div className="min-w-0">
                  <DialogTitle className="text-xl font-extrabold tracking-tight text-gray-900">
                    {title}
                  </DialogTitle>
                  {!!subtitle && (
                    <p className="mt-0.5 text-sm font-semibold text-gray-500">
                      {subtitle}
                    </p>
                  )}
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="grid h-10 w-10 flex-none place-items-center rounded-xl bg-gray-50 text-gray-700 hover:bg-gray-75"
                >
                  <span className="sr-only">Stäng</span>
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="overflow-y-auto px-5 py-4">{children}</div>

              {!!footer && (
                <div className="flex gap-3 border-t border-gray-75 px-5 py-3.5">
                  {footer}
                </div>
              )}
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </Transition>
);
